import { SkeletonLoader } from "@pepperstone/pepper-ui-kit";
import { type ReactNode, useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { AmountCurrencyResponse } from "@/api/cashier";
import { Icon } from "@/components/general/icon/Icon";
import { useFormatAmount } from "@/hooks/useFormatAmount";
import { EIcon } from "@/utils/enums";
import { isAmountCurrencyResponse, moneyToMajorUnits } from "@/utils/helpers";
import styles from "./BreakdownTransactionList.module.css";

export type BreakdownTransactionListItem = {
  name: string;
  money: AmountCurrencyResponse;
  money_in_processing_currency?: AmountCurrencyResponse;
  fee?: AmountCurrencyResponse;
};

export type BreakdownTransactionListProps = {
  title: ReactNode;
  total?: AmountCurrencyResponse | ReactNode;
  totalInProcessingCurrency?: AmountCurrencyResponse;
  totalTestId?: string;
  items?: BreakdownTransactionListItem[];
  collapsed?: boolean;
  updatingProcessingCurrency?: boolean;
  transactionIndexOffset?: number;
  /** Prefix for per-item amount data-testid attributes. Produces `{prefix}-{index}` e.g. `breakdown-refund-0`. */
  itemTestIdPrefix?: string;
  /** When provided, renders an info notice block below the header row. */
  feeDescription?: ReactNode;
};

const PLACEHOLDER_WIDTH_MD = 12;
const PLACEHOLDER_WIDTH_LG = 20;

export const BreakdownTransactionList = ({
  title,
  total,
  totalInProcessingCurrency,
  totalTestId,
  items,
  collapsed,
  updatingProcessingCurrency,
  transactionIndexOffset = 0,
  itemTestIdPrefix,
  feeDescription,
}: BreakdownTransactionListProps) => {
  const { formatAmount } = useFormatAmount();
  const { t } = useTranslation();

  const renderAmount = useCallback(
    (
      money: AmountCurrencyResponse,
      moneyInProcessingCurrency: AmountCurrencyResponse | null | undefined,
      placeholderWidth: number
    ) => {
      const base = formatAmount(moneyToMajorUnits(money), money);
      if (
        moneyInProcessingCurrency === null ||
        moneyInProcessingCurrency === undefined
      ) {
        return base;
      }
      const proc = formatAmount(
        moneyToMajorUnits(moneyInProcessingCurrency),
        moneyInProcessingCurrency
      );

      if (updatingProcessingCurrency) {
        return (
          <span className="ps-flex ps-gap-2">
            <span>{base}</span>
            <SkeletonLoader
              variant="rectangular"
              width={placeholderWidth}
              height={4}
            />
          </span>
        );
      }
      return `${base} (${proc})`;
    },
    [formatAmount, updatingProcessingCurrency]
  );

  return (
    <div>
      <div className={styles.BreakdownDetailsListHeader}>
        <span>{title}</span>
        {total !== undefined && total !== null && (
          <span className="ps-text-right" data-testid={totalTestId}>
            {isAmountCurrencyResponse(total)
              ? renderAmount(
                  total,
                  totalInProcessingCurrency,
                  PLACEHOLDER_WIDTH_LG
                )
              : total}
          </span>
        )}
      </div>
      {feeDescription && (
        <div className={styles.FeeDescription}>
          <Icon name={EIcon.InformationBlue} />
          <span>{feeDescription}</span>
        </div>
      )}
      {!collapsed && items && (
        <div className="ps-flex ps-flex-col ps-gap-2 ps-pt-2 ps-pl-2">
          {items.map((item, i) => (
            <div
              data-testid={"breakdown-transaction-list-item"}
              key={`breakdown-transaction-list-item-${i}`}
              className={`${styles.BreakdownDetailsListItem}`}
            >
              <span>
                {item.name ||
                  t("withdrawals.summary.breakdown.transaction_name", {
                    index: 1 + i + transactionIndexOffset,
                  })}
              </span>
              <span
                className="ps-text-right"
                data-testid={
                  itemTestIdPrefix
                    ? `${itemTestIdPrefix}-${i + transactionIndexOffset}`
                    : `breakdown-transaction-list-item-amount-${item.name.replace(/\s+/g, "-").toLowerCase()}`
                }
              >
                {renderAmount(
                  item.money,
                  item.money_in_processing_currency,
                  PLACEHOLDER_WIDTH_MD
                )}
                {item.fee && (
                  <>
                    {" "}
                    {t("withdrawals.fee.transaction_fee", {
                      value: formatAmount(
                        moneyToMajorUnits(item.fee),
                        item.fee
                      ),
                    })}
                  </>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
